import { createClient } from '@owner/olympus-client'
import { type AxiosError } from 'axios'
import { storeToRefs } from 'pinia'

import { useAuthStore } from '@/auth/useAuthStore'
import { useEmitter } from '@/utils/useEmitter'

import { getIframeSession } from './auth/iframeSession'
import { useCoreStore } from './core/store/useCoreStore'

export const VITE_OLYMPUS_URL = import.meta.env.VITE_OLYMPUS_URL

export const OlympusClient = createClient(
  VITE_OLYMPUS_URL,
  async () => {
    const { activeBrandId } = storeToRefs(useCoreStore())
    const audienceVersion = import.meta.env.APP_SHA || 'unknown'

    // When Hermes is embedded in another Owner app's iframe (e.g. Hercules'
    // Brand Status tool) the auth cookie can't cross origins, so the parent
    // hands us a bearer token. Identify as 'mobile' so the backend reads the
    // Authorization header instead of the (absent) cookie, and use the parent's
    // audience so the token validates.
    const iframeSession = getIframeSession()
    if (iframeSession) {
      return {
        activeBrandId: activeBrandId.value ?? null,
        audience: iframeSession.audience,
        audienceVersion,
        bearerToken: iframeSession.accessToken,
        client: 'mobile',
      }
    }

    const authStore = useAuthStore()
    return {
      activeBrandId: activeBrandId.value ?? null,
      audience: 'hermes',
      audienceVersion,
      client: 'web',
      csrfToken: authStore.csrfToken ?? undefined,
    }
  },
  {
    onResponseError: async (
      err: AxiosError<{ details?: { logout?: boolean; type?: string } }>
    ) => {
      if (err.status === 401 && err.response?.data?.details?.logout) {
        // Emits to useSessionManager
        useEmitter.emit('auth.logout')
      }

      // Olympus returns this when a brand-scoped request is denied because the
      // caller isn't a member of the active brand (e.g. access was removed
      // while a tab stayed open on a now-stale activeBrandId). Reconcile the
      // session to an accessible brand rather than surfacing a dead-end 403.
      if (
        err.status === 403 &&
        err.response?.data?.details?.type === 'no-brand-access'
      ) {
        // Handled in useSessionManager
        useEmitter.emit('auth.brand-access-lost')
      }

      throw err
    },
  }
)

export interface PaginatedResult<TResource> {
  limit: number
  page: number
  pageCount: number
  results: TResource[]
  total: number
}
